import { MAX_PITCH, type AttackType, type Fighter, type FighterInput } from '@archon3d/core';
import type { FighterIntent } from '../../input/fighterInput';

/** Closer than this to the cursor, the bearing is guesswork: keep the facing rather than spin. */
const POINT_BLANK = 0.3;
/**
 * How long after an attack leaves a fighter goes on facing the cursor it was aimed at: a blow's
 * wind-up (it lands along the facing it then has, D-42) and a moment more, so a swing or a shot is
 * seen going where it went before the fighter turns back to its path.
 */
export const ATTACK_FACING_SECONDS = 0.35;
/** Attacks that strike all round the fighter: no cursor aims them, so none turns it for them. */
const ALL_ROUND: ReadonlySet<AttackType> = new Set<AttackType>(['aura', 'burst', 'burn']);

/** Where a fighter's eye sits, matching the simulation's own `eyePosition`. */
function eyeHeight(f: Fighter): number {
  return f.pos.y + f.height * 0.9;
}

/**
 * The elevation that points a shot at the enemy's middle. The player never sets this: under a
 * fixed camera there is no up and down to aim with, and a shooter that could not hit a flier
 * without one would be unplayable.
 */
export function automaticPitch(me: Fighter, enemy: Fighter | null): number {
  if (!enemy) return 0;
  const flat = Math.hypot(enemy.pos.x - me.pos.x, enemy.pos.z - me.pos.z);
  const rise = enemy.pos.y + enemy.height / 2 - eyeHeight(me);
  const pitch = Math.atan2(rise, Math.max(flat, 1e-3));
  return Math.max(-MAX_PITCH, Math.min(MAX_PITCH, pitch));
}

/**
 * Which way a fighter faces this step. It turns the way it moves (the owner, 2026-09-26), where it
 * used to face the cursor wherever it went and so walked backwards or sideways away from it.
 *
 * A stick names a direction and is taken as it is: a pad's or a headset's right stick aims, and a
 * lone stick or a thumb on the screen is the way the fighter goes. A cursor names a place, which
 * the fighter turns to only for an attack that needs aiming: while it is pressed or held, while a
 * blow winds up, and for `ATTACK_FACING_SECONDS` after. Otherwise the fighter faces where it moves,
 * and keeps its facing when it stands.
 */
export function facingOf(intent: FighterIntent, me: Fighter): number {
  if (intent.aimDir && Math.hypot(intent.aimDir.x, intent.aimDir.z) > 1e-3)
    return Math.atan2(intent.aimDir.x, intent.aimDir.z);
  if (intent.aimAt && aiming(intent, me)) {
    const dx = intent.aimAt.x - me.pos.x;
    const dz = intent.aimAt.z - me.pos.z;
    return Math.hypot(dx, dz) > POINT_BLANK ? Math.atan2(dx, dz) : me.yaw;
  }
  if (Math.hypot(intent.moveX, intent.moveZ) > 1e-3) return Math.atan2(intent.moveX, intent.moveZ);
  return me.yaw;
}

/** An aimed attack pressed or held now, a blow winding up, or one that left a moment ago. */
function aiming(intent: FighterIntent, me: Fighter): boolean {
  if (ALL_ROUND.has(me.attackType)) return false;
  if (intent.attack || (me.windup ?? 0) > 0) return true;
  return me.cooldown > 0 && me.attackInterval - me.cooldown < ATTACK_FACING_SECONDS;
}

/**
 * Turns a screen-relative intent into the fighter-local input the simulation takes.
 *
 * The player pushes a direction on the screen and points at a place; the simulation wants
 * "forward" and "strafe" relative to the fighter's own yaw. Resolving the facing first and then
 * rotating the movement into it is what lets a fighter step aside while it aims an attack.
 */
export function intentToInput(
  intent: FighterIntent,
  me: Fighter,
  enemy: Fighter | null,
): FighterInput {
  const yaw = facingOf(intent, me);
  const sin = Math.sin(yaw);
  const cos = Math.cos(yaw);
  return {
    moveZ: intent.moveX * sin + intent.moveZ * cos,
    moveX: intent.moveX * cos - intent.moveZ * sin,
    yaw,
    pitch: automaticPitch(me, enemy),
    dash: intent.dash,
    attack: intent.attack,
    steer: intent.steer ?? 0,
  };
}
